// Every company on record. Search and filters live in the URL.
import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../../../lib'
import { Busy, Button, Err, Page, Table, useCursor, type Column } from '../kit'
import { CARD_WORD, TRACK_LABEL, nameOf, queryOf, rememberList, useFilters, usePaged, useMeta, useWrite, type Imported, type Stages, type Sync, type Track } from './lib'
import { Check, FilterBar, LoadMore, ReadLine, SearchField, Select, lastTouch, useForm } from './parts'

type TrackRow = { track: Track; stage: string; stage_label: string; owner: string | null; hold_until: string | null }
type Row = {
  id: string; name: string; domain: string | null; funding_stage: string | null; yc_batch: string | null; do_not_contact: boolean; people: number
  tracks: TrackRow[]; twin: boolean; last_at: string | null; last_actor: string | null; last_channel: string | null; last_summary: string | null; card_id: string | null; card_status: string | null
}
type Data = { at: string; rows: Row[]; total: number; truncated: boolean; next: string | null; stages: Stages; sync: Sync; imported: Imported }

const COLUMNS: Column<Row>[] = [
  { key: 'name', head: 'Company', primary: true, cell: (r) => <><Link className="st-strong" to={`/staff/companies/${r.id}`}>{r.name}</Link>{r.do_not_contact ? <span className="st-bad"> Do not contact</span> : null}{r.twin ? <span className="st-warn" data-testid="st-twin-row"> Maybe a twin</span> : null}</> },
  { key: 'domain', head: 'Domain', phone: false, cell: (r) => r.domain },
  { key: 'tracks', head: 'Tracks', cell: (r) => r.tracks.length ? <span>{r.tracks.map((t) => `${TRACK_LABEL[t.track]}: ${t.stage_label}${t.owner ? ` (${nameOf(t.owner)})` : ''}`).join(', ')}</span> : null },
  { key: 'funding', head: 'Funding', phone: false, nowrap: true, cell: (r) => r.funding_stage },
  { key: 'people', head: 'People', phone: false, num: true, cell: (r) => r.people },
  { key: 'last', head: 'Last touch', nowrap: true, cell: lastTouch },
  { key: 'card', head: 'Card', nowrap: true, cell: (r) => (r.card_id ? <Link className="st-link" to={`/staff/card/${r.card_id}`}>{CARD_WORD[r.card_status ?? ''] ?? r.card_status}</Link> : null) },
]

export function CompaniesPage() {
  const { sp, get, put } = useFilters()
  const navigate = useNavigate()
  const meta = useMeta()
  const form = useForm()
  const write = useWrite()
  const search = queryOf(sp, ['q', 'track', 'stage', 'account', 'internal'])
  const q = usePaged<Data, Row>(['crm', 'companies', search], `/api/staff/crm/companies${search}`, 60_000)
  const { rows, head } = q
  const { cur, setCur } = useCursor(rows, (r) => r.id, (r) => navigate(`/staff/companies/${r.id}`))
  useEffect(() => { if (rows.length) rememberList('company', rows.map((r) => r.id)) }, [rows])
  const track = get('track') as Track | ''
  const stageOptions = track && meta.data ? meta.data.stages[track] : []

  const add = async () => {
    const v = await form.open({ title: 'Add a company', confirm: 'Add company', fields: [{ key: 'name', label: 'Name', required: true }, { key: 'domain', label: 'Domain', placeholder: 'acme.com' }] })
    if (!v) return
    const r = await write<{ id: string }>('POST', '/api/staff/crm/companies', { name: v.name, domain: v.domain.trim() || null }, 'Company added')
    if (r) navigate(`/staff/companies/${r.id}`)
  }

  return (
    <Page title="Companies" testid="st-companies" sub={<ReadLine at={head?.at} sync={head?.sync} what="Records" imported={head?.imported} />} right={<Button onClick={add} data-testid="st-add-company">Add company</Button>}>
      <FilterBar
        on={['track', 'stage', 'account', 'internal'].filter((k) => get(k)).length}
        more={<Check label="Show internal" value={get('internal') === '1'} testid="st-internal" onChange={(v) => put({ internal: v ? '1' : null })} />}
        search={<SearchField value={get('q')} onChange={(v) => put({ q: v })} placeholder="Search name or domain" testid="st-q" />}
      >
        <Select label="Track" value={track} testid="st-track" onChange={(v) => put({ track: v, stage: null })} options={[{ value: '', label: 'Any track' }, ...(Object.keys(TRACK_LABEL) as Track[]).map((t) => ({ value: t, label: TRACK_LABEL[t] }))]} />
        <Select label="Stage" value={get('stage')} testid="st-stage" onChange={(v) => put({ stage: v })} options={[{ value: '', label: 'Any stage' }, ...stageOptions.map((s) => ({ value: s.key, label: s.label }))]} />
        <Select label="Account" value={get('account')} testid="st-account" onChange={(v) => put({ account: v })} options={[{ value: '', label: 'Any account' }, { value: 'card', label: 'Card request' }, { value: 'signed_up', label: 'Account only' }, { value: 'none', label: 'Not signed up' }]} />
        {head ? <span className="st-muted">{head.total.toLocaleString('en-US')} companies</span> : null}
      </FilterBar>
      {q.error ? <Err error={q.error} /> : !head ? <Busy /> : (
        <>
          <Table rows={rows} columns={COLUMNS} rowKey={(r) => r.id} cur={cur} onPick={(r) => setCur(r.id)} onOpen={(r) => navigate(`/staff/companies/${r.id}`)} testid="st-companies-table" empty="No company matches." />
          <LoadMore shown={rows.length} total={head.total} more={q.more} loading={q.loading} load={q.load} />
        </>
      )}
      {form.node}
    </Page>
  )
}
